import { useState, useRef, useEffect } from 'react';
import { CalendarIcon, ChevronUp, ChevronDown } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

function getDaysGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells = [];

  // Previous month trailing days
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: prevDays - i, current: false });
  }
  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }
  // Next month leading days - always fill to 42 (6 rows)
  while (cells.length < 42) {
    cells.push({ day: cells.length - firstDay - daysInMonth + 1, current: false });
  }
  return cells;
}

export default function DatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [monthPicker, setMonthPicker] = useState(false);
  const ref = useRef(null);

  const today = new Date();
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(selected?.getFullYear() || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? today.getMonth());

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setMonthPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const selectDay = (day) => {
    const yr = viewYear;
    const mo = String(viewMonth + 1).padStart(2, '0');
    const dy = String(day).padStart(2, '0');
    onChange(`${yr}-${mo}-${dy}`);
    setOpen(false);
    setMonthPicker(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
    setMonthPicker(false);
  };

  const handleToday = () => {
    const t = new Date();
    const yr = t.getFullYear();
    const mo = String(t.getMonth() + 1).padStart(2, '0');
    const dy = String(t.getDate()).padStart(2, '0');
    onChange(`${yr}-${mo}-${dy}`);
    setOpen(false);
    setMonthPicker(false);
  };

  const displayValue = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-US')
    : '';

  const cells = getDaysGrid(viewYear, viewMonth);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const selectMonth = (m) => {
    setViewMonth(m);
    setMonthPicker(false);
  };

  // Years for month picker
  const years = [];
  for (let y = viewYear - 3; y <= viewYear + 4; y++) years.push(y);

  return (
    <div className="relative" ref={ref} data-testid="date-picker">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setOpen(!open); setMonthPicker(false); }}
          className="flex-1 h-10 rounded-lg border bg-background px-3 text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="date-picker-trigger"
        >
          <span className={displayValue ? 'text-foreground' : 'text-muted-foreground'}>
            {displayValue || 'mm/dd/yyyy'}
          </span>
          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
        </button>
        <button
          type="button"
          onClick={handleToday}
          className="h-10 px-4 shrink-0 rounded-lg border bg-background text-sm font-medium hover:bg-muted transition-colors"
          data-testid="date-picker-today-btn"
        >
          Today
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-12 z-50 bg-card border rounded-lg shadow-xl p-3 w-[280px]" data-testid="date-picker-dropdown">
          {!monthPicker ? (
            <>
              {/* Month/Year header */}
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={() => setMonthPicker(true)}
                  className="text-sm font-semibold hover:text-primary transition-colors flex items-center gap-1"
                  data-testid="date-picker-month-label"
                >
                  {MONTHS[viewMonth]} {viewYear}
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-muted transition-colors" data-testid="date-picker-prev">
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-muted transition-colors" data-testid="date-picker-next">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-0 mb-1">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d}</div>
                ))}
              </div>

              {/* Day grid - always 6 rows */}
              <div className="grid grid-cols-7 gap-0">
                {cells.map((cell, i) => {
                  const isToday = cell.current && `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}` === todayStr;
                  const isSelected = cell.current && value === `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => cell.current && selectDay(cell.day)}
                      disabled={!cell.current}
                      className={`h-8 w-full text-xs rounded transition-colors
                        ${!cell.current ? 'text-muted-foreground/30 cursor-default' : 'hover:bg-muted cursor-pointer'}
                        ${isToday ? 'bg-primary text-primary-foreground font-bold' : ''}
                        ${isSelected && !isToday ? 'bg-primary/20 font-bold' : ''}
                      `}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t">
                <button type="button" onClick={handleClear} className="text-xs text-primary hover:underline font-medium" data-testid="date-picker-clear">
                  Clear
                </button>
                <button type="button" onClick={handleToday} className="text-xs text-primary hover:underline font-medium" data-testid="date-picker-today-inline">
                  Today
                </button>
              </div>
            </>
          ) : (
            /* Month/Year picker */
            <div className="max-h-[280px] overflow-y-auto" data-testid="date-picker-month-view">
              {years.map(yr => (
                <div key={yr} className="mb-2">
                  <p className="text-xs font-bold text-muted-foreground mb-1">{yr}</p>
                  <div className="grid grid-cols-4 gap-1">
                    {MONTHS.map((m, mi) => (
                      <button
                        key={mi}
                        type="button"
                        onClick={() => { setViewYear(yr); selectMonth(mi); }}
                        className={`text-xs py-1.5 rounded transition-colors
                          ${yr === viewYear && mi === viewMonth ? 'bg-primary text-primary-foreground font-bold' : 'hover:bg-muted'}
                        `}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
